import type { ComponentProps, FunctionComponent, ReactNode, Ref } from 'react'
import React, { Fragment, useState } from 'react'

import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import { useAppDispatch } from '@app/utils/redux'
import { sleep } from '@app/utils/sleep'

import type { AnalyticsAction } from '@app/packages/analytics/declaration'
import { spawn } from '@app/packages/disposable/spawn'

import { useDisposableEffect } from '@app/hooks/useDisposableEffect'
import { useEvent } from '@app/hooks/useEvent'

import { getAnalyticsRouter } from '@app/store/actions/analytics'

import type { ButtonType } from './ButtonType'
import { ButtonTypePrimary } from './ButtonType'

import classes from './Button.module.scss'

export type ButtonAnimation = { className: string; duration: number }

export type ButtonProps = Omit<ComponentProps<'button'>, 'size' | 'type' | 'href'> & {
  /** The event and the handler it belongs to, so a control can only report what it can also do. */
  action: AnalyticsAction<[React.MouseEvent<HTMLButtonElement>]>
  type?: ButtonType
  animation?: ButtonAnimation
  /**
   * Replacement for type property of button as type already occupied.
   *
   * Primary usage of this prop is to forbid form submit on button click
   * by setting htmlType: "button"
   * https://stackoverflow.com/questions/2825856/html-button-to-not-submit-form
   */
  htmlType?: ComponentProps<'button'>['type']
  render?: (props: ComponentProps<'button'>, ref?: Ref<HTMLButtonElement>) => ReactNode
  ref?: Ref<HTMLButtonElement>
}

export const Button: FunctionComponent<ButtonProps> = ({
  action,
  type = new ButtonTypePrimary(),
  animation: panimation,
  className,
  htmlType,
  children,
  onClick: pOnClick,
  render,
  ref,
  ...rprops
}) => {
  const dispatch = useAppDispatch()
  const [animation, setAnimation] = useState(panimation ? panimation : undefined)
  const onClick = useEvent((e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(getAnalyticsRouter()).send(action.event)
    action.handler(e)
    pOnClick?.(e)
  })

  useDisposableEffect(
    stack => {
      stack.defer(() => setAnimation(undefined))
      if (!panimation) return

      stack.use(
        spawn(async stack => {
          const abortController = stack.adopt(new AbortController(), ab => ab.abort())
          // A class put back in the frame it came off in does not restart the animation.
          await sleep(30, abortController.signal)
          setAnimation(panimation)
          await sleep(panimation.duration, abortController.signal)
          setAnimation(undefined)
        }, reportSpawnFailure('Button'))
      )
    },
    [panimation]
  )

  const image = type.getImage?.()

  const inside = image ? (
    <Fragment>
      {image} {children}
    </Fragment>
  ) : (
    children
  )

  const bprops = {
    className: cn(classes.button, type.getClassName(), animation ? animation.className : null, className),
    onClick,
    type: htmlType,
    ...rprops,
  }

  if (render) return render(bprops, ref)

  return (
    <button {...bprops} ref={ref}>
      {inside}
    </button>
  )
}
